import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Typography, Button, Spin, notification, Collapse, Rate, Input } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';

import { useTheme } from '../../theme/ThemeProvider';
import { httpService } from '../../services/httpService';
import { 
  getTripItemTypePersian
} from '../../utils/tripUtils';
import './styles.css';
import TripCard from '../../components/TripCard';

const { Text } = Typography;
const { TextArea } = Input;

// TripState enum
enum TripState {
  None = 0,              // نامشخص
  Requested = 1,         // درخواست
  PrimaryReserve = 2,    // رزرو اولیه
  DriverAssigned = 3,    // تخصیص راننده
  FinalReserve = 4,      // رزرو نهایی
  Start = 5,             // در حال سفر
  End = 6                // پایان سفر
}

// Function to convert English numbers to Persian
const toPersianNumbers = (num: number): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return num.toString().replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
};

// Function to convert string with numbers to Persian
const toPersianString = (str: string): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
};

interface TripProps {
  tripId?: string;
}

interface Driver {
  id: number;
  isDelete: boolean;
  dateOfCreat: string;
  driverName: string;
  gender: boolean;
  mobile: string;
  area: number;
  restrictions: boolean;
  carModel: string;
  carColor: string;
  plate1: number;  // دو رقم اول (مثل 52)
  plate2: string;  // حرف (مثل د)
  plate3: number;  // سه رقم (مثل 689)
  plate4: number;  // عدد پایین بخش راست (مثل 11)
}

interface TripDetails {
  id?: string;
  type?: number;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  source?: {
    id?: string;
    address?: string;
  };
  destination?: {
    id?: string;
    address?: string;
  };
  state?: number;
  createdAt?: string;
  tripItems?: any[];
  selectedOption?: {
    price?: number;
  };
  driverId?: number;
  driver?: Driver;
  supportCode?: string;
}



const Trip: React.FC<TripProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tripId: urlTripId } = useParams<{ tripId: string }>();
  const { theme } = useTheme();
  const [tripDetails, setTripDetails] = useState<TripDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  
  // Get tripId from URL params first, then fallback to location state or query params
  const tripId = urlTripId || location.state?.tripId || new URLSearchParams(location.search).get('tripId');

  // Fetch trip details from API
  const fetchTripDetails = async (id: string) => {
    try {
      console.log('📡 Fetching trip details , tripId:', id);
      const response = await httpService.getTrip(id);
      console.log('✅ Trip API response:', response);
      
      if (response && response.data) {
        setTripDetails(response.data);
      } else if (response) {
        setTripDetails(response);
      } else {
        notification.error({ message: 'اطلاعات سفر یافت نشد' });
        navigate('/home');
      }
    } catch (error) {
      console.error('❌ Error fetching trip details :', error);
      notification.error({ message: 'خطا در دریافت اطلاعات سفر' });
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔍 Trip useEffect triggered with tripId:', tripId);
    console.log('🔍 Location state:', location.state);
    
    if (tripId) {
      // Always fetch trip details from API using the tripId
      console.log('📡 Fetching trip details from API with tripId:', tripId);
      fetchTripDetails(tripId);
    } else {
      notification.error({ message: 'شناسه سفر یافت نشد' });
      navigate('/home');
    }
  }, [tripId]);

  const handleBackToHome = () => {
    navigate('/home');
  };

  // Handle rating submission
  const handleSubmitRating = async () => {
    if (rating === 0) {
      notification.warning({ message: 'لطفا امتیاز خود را وارد کنید' });
      return;
    }
    
    if (!tripId) {
      notification.error({ message: 'شناسه سفر یافت نشد' });
      return;
    }
    
    try {
      await httpService.rateTrip(tripId, rating, comment);
      notification.success({ message: 'نظر شما با موفقیت ثبت شد' });
      console.log('Rating submitted successfully:', { tripId, rating, comment });
    } catch (error) {
      console.error('Rating submission error:', error);
      notification.error({ message: 'خطا در ثبت نظر' });
    }
  };

  // Get state message
  const getStateMessage = () => {
    const state = tripDetails?.state || 0;
    
    switch (state) {
      case TripState.Requested:
        return {
          type: 'info',
          message: 'در انتظار تایید سفر، کارشناس سفر با شما تماس خواهد گرفت'
        };
      
      case TripState.PrimaryReserve:
        return {
          type: 'warning',
          message: 'در انتظار پرداخت'
        };
      
      case TripState.DriverAssigned:
        return {
          type: 'info',
          message: 'در جستجوی راننده، کارشناس سفر با شما تماس خواهد گرفت'
        };
      
      case TripState.FinalReserve:
        return {
          type: 'success',
          message: 'پرداخت با موفقیت انجام شده، در انتظار شروع سفر'
        };
      
      case TripState.Start:
        // Check if there are unpaid items
        const hasUnpaidItems = tripDetails?.tripItems?.some((item: any) => item.isPaid === false);
        
        if (hasUnpaidItems) {
          return {
            type: 'warning',
            message: 'گزینه‌های سفر اضافه شد، منتظر پرداخت شما هستیم'
          };
        }
        
        return {
          type: 'info',
          message: 'سفر در حال انجام است و درصورت نیاز به تغییر در گزینه‌های سفر راننده در کنار شماست'
        };
      
      case TripState.End:
        return {
          type: 'success',
          message: 'ممنون از همراهی شما'
        };
      
      default:
        return null;
    }
  };

  // Calculate total price from trip items
  const calculateTotalPrice = (): number => {
    if (!tripDetails?.tripItems || tripDetails.tripItems.length === 0) {
      return tripDetails?.selectedOption?.price || 0;
    }
    
    return tripDetails.tripItems.reduce((total: number, item: any) => {
      const itemPrice = item.tripBaseAmount?.unitPrice || 0;
      const itemCount = item.count || 1;
      return total + (itemPrice * itemCount);
    }, 0);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16 }}>در حال دریافت اطلاعات سفر...</Text>
      </div>
    );
  }

  if (!tripDetails) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Text>اطلاعات سفر یافت نشد</Text>
        <Button onClick={handleBackToHome} style={{ marginTop: 16 }}>
          بازگشت به خانه
        </Button>
      </div>
    );
  }

  const stateMessage = getStateMessage();
  const currentState = tripDetails?.state || 0;
  const shouldShowPayment = currentState === TripState.PrimaryReserve || currentState === TripState.Start;
  const shouldShowDriver = (currentState === TripState.FinalReserve || currentState === TripState.Start) && tripDetails?.driver;

  return (
    <div className="trip-container">

      {/* State Message */}
      {stateMessage && (
        <div className={`state-message state-message-${stateMessage.type}`}>
          <Text style={{ fontSize: 15, fontWeight: 500 }}>
            {stateMessage.message}
          </Text>
          {/* Support Contact Button for State 5 */}
          {currentState === TripState.Start && (
            <Button
              type="default"
              icon={<PhoneOutlined />}
              href="tel:02191004644"
              style={{ marginTop: 12, width: '100%' }}
            >
              تماس با پشتیبانی
            </Button>
          )}
        </div>
      )}

      {/* Trip Card Header */}
      <div className="trip-header-section">
        <TripCard 
          trip={{
            id: tripDetails.id || 'temp-id',
            type: tripDetails.type || 1,
            startDate: tripDetails.startDate || '',
            startTime: tripDetails.startTime || '',
            endDate: tripDetails.endDate || '',
            endTime: tripDetails.endTime || '',
            durationMinutes: 0,
            source: {
              id: tripDetails.source?.id || 'temp-source',
              address: tripDetails.source?.address || 'آدرس مبدا'
            },
            destination: {
              id: tripDetails.destination?.id || 'temp-dest',
              address: tripDetails.destination?.address || 'آدرس مقصد'
            },
            state: tripDetails.state || 0,
            createdAt: tripDetails.createdAt || '',
            supportCode: tripDetails.supportCode
          }}
          className="trip-header-trip-card"

        />
        

      </div>



       {/* Service Details with Collapse */}
      <div className="service-details-section">
        <Collapse 
          defaultActiveKey={[]}
          style={{
            padding: '0px 0px',
            margin: '0px 0px',
          }}
        >
          <Collapse.Panel 
            key="1" 
            header={
              <Row gutter={8} align="middle" style={{ width: '100%' }}>
                
                <Col span={12}>
                  <Text strong style={{ color: theme.text.primary }}>
                    مجموع هزینه ها
                  </Text>
                </Col>
                <Col span={12} style={{ textAlign: 'left' }}>
                  <Text strong style={{ fontSize: '16px', color: theme.primary, whiteSpace: 'nowrap' }}>
                    {toPersianString(calculateTotalPrice().toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ','))} تومان
                  </Text>
                </Col>
              </Row>
            }
            style={{
              border: 'none',
              backgroundColor: 'transparent'
            }}
          >
            {/* Trip Items (Services) */}
            {tripDetails.tripItems && tripDetails.tripItems.length > 0 && (
              <div  >
                {tripDetails.tripItems.map((item: any, index: number) => (
                  <Row key={index} align="middle" className="row-info-field">
                    <Col span={16}>
                      <Text style={{ color: theme.text.primary, fontSize: '14px' }}>
                        {getTripItemTypePersian(item.tripBaseAmount?.tripItemType)}
                        {item.count > 0 && (
                          <span style={{ color: theme.text.secondary, marginLeft: 8, fontSize: '13px' }}>
                            {item.count > 1 && item.tripBaseAmount?.tripItemType !== 102 && item.tripBaseAmount?.tripItemType !== 2 && item.tripBaseAmount?.tripItemType !== 1 ?
                             ` (${toPersianNumbers(item.count)} عدد)` : ''}
                            {item.count > 1 && (item.tripBaseAmount?.tripItemType === 102 || item.tripBaseAmount?.tripItemType === 2) ?
                             ` ${toPersianString(item.count.toString())} ` : ''}
                            {item.count > 1 && item.tripBaseAmount?.tripItemType === 1 ? 
                            ` (${toPersianString(item.count.toFixed(2))} کیلومتر)` : ''}
                            {item.count > 1 && item.tripBaseAmount?.tripItemType === 2 && ' ساعت'}
                            {item.count > 1 && item.tripBaseAmount?.tripItemType === 102 && ' دقیقه'}
                          </span>
                        )}
                      </Text>
                    </Col>
                    <Col span={8} style={{ textAlign: 'left' }}>
                      <Text strong style={{ color: theme.text.primary, fontSize: '15px' }}>
                        {item.tripBaseAmount?.unitPrice ? `${toPersianString((item.tripBaseAmount.unitPrice * item.count).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ','))} تومان` : 'قیمت نامشخص'}
                      </Text>
                    </Col>
                  </Row>
                ))}
              </div>
            )}
          </Collapse.Panel>
        </Collapse>
      </div>

      {/* Driver Information Card - Only show in states 4 and 5 */}
      {shouldShowDriver && (() => {
        const driver = tripDetails!.driver!;
        return (
          <div className="driver-info-section" >
          <Collapse 
            defaultActiveKey={[]}
          >
            <Collapse.Panel 
              key="1" 
              header={
                  <Row gutter={4} align="middle" >
                    <Col span={4}>
                      <Text strong style={{ color: theme.text.primary, fontSize: 14 }}>
                      خودرو
                    </Text>
                  </Col>
                    <Col span={20} style={{ textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Text style={{ color: theme.text.secondary, fontSize: 14 }}>
                          {driver.carModel} 
                      </Text>
                      <Text style={{ color: theme.text.secondary, fontSize: 14 }}>
                          {driver.carColor}
                      </Text>
                        <div className="license-plate iranian-format" style={{ minWidth: 'auto', padding: '0px px',margin: '0px', fontSize: 14 }}>
                        <div className="">
                            <span>{toPersianNumbers(driver.plate4).padStart(2, '۰')}</span>
                        </div>
                        <div className="plate-divider" style={{ height: 24, margin: '0 8px' }}></div>
                          <div className="plate-main-section" style={{ gap: 8 }}>
                            <span>{toPersianNumbers(driver.plate3).padStart(3, '۰')}</span>
                            <span>{driver.plate2}</span>
                            <span>{toPersianNumbers(driver.plate1).padStart(2, '۰')}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              }
          
            >
              <Row >
              {/* Mobile Number */}
              <Col span={24}>
                  <div className="row-info-field">
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text style={{ color: theme.text.secondary, fontSize: '14px' }}>
                        شماره تماس
                      </Text>
                    </Col>
                    <Col>
                      <a 
                          href={`tel:${driver.mobile}`}
                        style={{ 
                          color: theme.primary, 
                          fontSize: '15px',
                          textDecoration: 'none',
                          fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                          {toPersianString(driver.mobile)}
                      </a>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Driver Name */}
              <Col span={24}>
                  <div className="row-info-field">
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text style={{ color: theme.text.secondary, fontSize: '14px' }}>
                        نام راننده
                      </Text>
                    </Col>
                    <Col>
                      <Text strong style={{ color: theme.text.primary, fontSize: '15px' }}>
                          {driver.driverName}
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Traffic Plan */}
              <Col span={24}>
                  <div className="row-info-field">
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text style={{ color: theme.text.secondary, fontSize: '14px' }}>
                        طرح ترافیک
                      </Text>
                    </Col>
                    <Col>
                      <Text strong style={{ 
                          color: driver.restrictions ? theme.success : theme.error, 
                        fontSize: '15px' 
                      }}>
                          {driver.restrictions ? 'دارد' : 'ندارد'}
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            </Collapse.Panel>
          </Collapse>
          </div>
        );
      })()}

      {/* Rating Component for State 6 (Trip Ended) */}
      {currentState === TripState.End && (
        <div className="rating-section">
          <div className="rating-container">
            <Text strong style={{ fontSize: 16, marginBottom: 16, display: 'block', textAlign: 'center' }}>
              لطفا تجربه خود را با ما به اشتراک بگذارید
            </Text>
            
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Rate 
                value={rating} 
                onChange={setRating}
                style={{ fontSize: 32 }}
              />
            </div>
            
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="نظر خود را بنویسید (اختیاری)"
              rows={4}
              style={{ marginBottom: 16 }}
            />
            
            <Button
              type="primary"
              size="large"
              onClick={handleSubmitRating}
              style={{ 
                width: '100%',
                height: 48,
                fontSize: 16,
                backgroundColor: theme.button.primary,
                borderColor: theme.button.primary
              }}
            >
              ثبت نظر
            </Button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons" style={{ marginTop: 16 }}>
        {/* Payment Button - Only show in states 2 and 5 */}
        {shouldShowPayment && (
        <Button 
          type="primary" 
          size="large" 
          style={{ 
            height: 56, 
            fontSize: 16, 
            fontWeight: 500,
            width: '100%',
            backgroundColor: theme.button.primary,
            borderColor: theme.button.primary
          }}
        >
          پرداخت
        </Button>
        )}
        
        <Button 
          type="default" 
          size="large" 
          onClick={handleBackToHome}
          style={{ 
            marginTop: shouldShowPayment ? 16 : 0,
            width: '100%'
          }}
        >
          بازگشت
        </Button>
      </div>
    </div>
  );
};

export default Trip;
